import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Shield, Trophy, DollarSign, ShieldAlert, ArrowLeft,
  Check, X, Trash2, Eye, BadgeCheck, Clock, Plus, Upload, Loader2, Pencil,
  UserCog, Film, Flag, ExternalLink, Twitter, KeyRound, Search, UserPlus, Megaphone, Image as ImageIcon
} from 'lucide-react';
import { API } from '../lib/constants';

const NAV = [
  { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'profiles', label: 'Player Profiles', icon: <Users className="h-4 w-4" /> },
  { id: 'teams', label: 'Teams', icon: <Shield className="h-4 w-4" /> },
  { id: 'users', label: 'Users & Admins', icon: <UserCog className="h-4 w-4" /> },
  { id: 'moderation', label: 'Moderation', icon: <Shield className="h-4 w-4" /> },
  { id: 'legends', label: 'NFL Legendz', icon: <Trophy className="h-4 w-4" /> },
  { id: 'audit', label: 'Audit Log', icon: <Clock className="h-4 w-4" /> },
];

const img = (u) => (u && u.startsWith('/api') ? `${API}${u}` : u);

const AdminPortal = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [legends, setLegends] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legendModal, setLegendModal] = useState(null); // null | {} | legendObj
  const [importingLegendz, setImportingLegendz] = useState(false);
  const [codes, setCodes] = useState({}); // { user_id: {code, email} } generated verification codes
  const [claimCodes, setClaimCodes] = useState({}); // { user_id: {code, username, name} }
  const [claimRequests, setClaimRequests] = useState([]);
  const [teams, setTeams] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState({});
  const [roster, setRoster] = useState({ open: false, url: '', scraping: false, data: null, selected: {}, importing: false, result: null });
  const [addPlayer, setAddPlayer] = useState({ open: false, url: '', team_id: '', loading: false });
  const [sel, setSel] = useState({}); // bulk-selected profiles: { [profile.id]: {id, user_id} }
  const P = { params: { admin_user_id: userId } };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a, p, u, r, l] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, P).catch(() => ({ data: {} })),
        axios.get(`${API}/api/admin/athletes`, { params: { admin_user_id: userId, limit: 2000 } }).catch(() => ({ data: { athletes: [] } })),
        axios.get(`${API}/api/admin/profiles/pending`, P).catch(() => ({ data: { profiles: [] } })),
        axios.get(`${API}/api/admin/users`, P).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/api/admin/moderation/reports`, P).catch(() => ({ data: { reports: [] } })),
        axios.get(`${API}/api/admin/legends`, P).catch(() => ({ data: { legends: [] } })),
      ]);
      setStats(s.data.stats || null);
      setAthletes(a.data.athletes || []);
      setPending(p.data.profiles || []);
      setUsers(u.data.users || []);
      setReports(r.data.reports || []);
      setLegends(l.data.legends || []);
      axios.get(`${API}/api/admin/impersonation-logs`, P).then(res => setLogs(res.data.logs || [])).catch(() => {});
      axios.get(`${API}/api/admin/claim-requests`, P).then(res => setClaimRequests(res.data.requests || [])).catch(() => {});
      axios.get(`${API}/api/admin/teams`, P).then(res => setTeams(res.data.teams || [])).catch(() => {});
      axios.get(`${API}/api/admin/duplicates`, P).then(res => setDuplicates(res.data.groups || [])).catch(() => {});
    } finally { setLoading(false); }
  };

  const assignTeam = async (userId2, teamId) => {
    try {
      await axios.post(`${API}/api/admin/athletes/${userId2}/team`, { team_id: teamId || null }, P);
      toast.success(teamId ? 'Player added to team' : 'Player removed from team');
      loadAll();
    } catch { toast.error('Could not update team'); }
  };

  const uploadTeamLogo = async (slug, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/admin/teams/${slug}/logo`, fd, { params: { admin_user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const loadTeamMembers = async (teamId) => {
    try {
      const res = await axios.get(`${API}/api/admin/rosters/${teamId}/members`, P);
      setTeamMembers(prev => ({ ...prev, [teamId]: res.data.members || [] }));
    } catch { /* ignore */ }
  };
  const toggleTeam = (teamId) => {
    const open = expandedTeam === teamId;
    setExpandedTeam(open ? null : teamId);
    if (!open) loadTeamMembers(teamId);
  };
  const addToRoster = async (teamId, userId2) => {
    try {
      await axios.post(`${API}/api/admin/rosters/${teamId}/members`, { player_user_id: userId2 }, P);
      toast.success('Player added to roster');
      await loadTeamMembers(teamId); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add player'); }
  };
  const removeFromRoster = async (teamId, userId2) => {
    try {
      await axios.delete(`${API}/api/admin/rosters/${teamId}/members/${userId2}`, P);
      toast.success('Player removed from roster');
      await loadTeamMembers(teamId); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove player'); }
  };
  const createRoster = async () => {
    const name = window.prompt('Roster name (e.g., Indy Elite 7-on-7):');
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/api/admin/rosters`, { school_name: name.trim(), team_name: '7-on-7', type: 'seven_on_seven' }, P);
      toast.success('Roster created');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create roster'); }
  };
  const renameTeam = async (t) => {
    const name = window.prompt('Rename team / roster:', t.school_name || '');
    if (name === null) return;
    try {
      await axios.put(`${API}/api/admin/teams/${t.id}`, { school_name: name.trim() }, P);
      toast.success('Team renamed');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); }
  };

  const resolveDuplicates = async (keepId, removePlayers) => {
    const removeIds = removePlayers.map(p => p.user_id);
    if (!removeIds.length) return;
    const live = removePlayers.filter(p => p.is_verified);
    let msg = `Delete ${removeIds.length} duplicate profile(s)? This keeps the selected one and removes the rest.`;
    if (live.length) {
      msg = `⚠️ WARNING: ${live.length} of the profiles you're about to DELETE are already claimed/live (${live.map(p => p.name).join(', ')}). Deleting a claimed profile removes a real athlete's page. Keep going?`;
    }
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/api/admin/profiles/bulk-delete`, { user_ids: removeIds }, P);
      toast.success('Duplicates removed');
      loadAll();
    } catch { toast.error('Could not remove duplicates'); }
  };

  const resolveClaimRequest = async (id) => {
    try {
      await axios.post(`${API}/api/admin/claim-requests/${id}/resolve`, null, P);
      setClaimRequests(prev => prev.filter(r => r.id !== id));
      toast.success('Marked as handled');
    } catch { toast.error('Could not update'); }
  };

  useEffect(() => { if (userId && isAdmin) loadAll(); else setLoading(false); /* eslint-disable-next-line */ }, []);

  // ── actions ──
  const setApproval = async (profile_id, status) => {
    try { await axios.put(`${API}/api/admin/profiles/${profile_id}/approval`, { status }, P); toast.success(`Profile ${status}`); loadAll(); }
    catch { toast.error('Failed'); }
  };
  // --- Bulk selection + actions ---
  const toggleSel = (p) => setSel(s => {
    const n = { ...s };
    if (n[p.id]) delete n[p.id]; else n[p.id] = { id: p.id, user_id: p.user_id };
    return n;
  });
  const clearSel = () => setSel({});
  const selList = Object.values(sel);
  const bulkSetApproval = async (status) => {
    if (selList.length === 0) return;
    try {
      const res = await axios.post(`${API}/api/admin/profiles/bulk-approval`, { profile_ids: selList.map(x => x.id), status }, P);
      toast.success(`${res.data.updated} profile(s) ${status}`);
      clearSel(); loadAll();
    } catch { toast.error('Bulk action failed'); }
  };
  const bulkDelete = async () => {
    if (selList.length === 0) return;
    if (!window.confirm(`Delete ${selList.length} profile(s)? This permanently removes their pages and data.`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/profiles/bulk-delete`, { user_ids: selList.map(x => x.user_id) }, P);
      toast.success(`${res.data.deleted} deleted${res.data.skipped ? `, ${res.data.skipped} skipped (admins)` : ''}`);
      clearSel(); loadAll();
    } catch { toast.error('Bulk delete failed'); }
  };
  const toggleVerify = async (profile_id, verified) => {
    try { await axios.put(`${API}/api/admin/athlete/${profile_id}/verify`, null, { params: { admin_user_id: userId, verified } }); toast.success(verified ? 'Verified' : 'Unverified'); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const genCode = async (p) => {
    try {
      const res = await axios.post(`${API}/api/admin/athletes/${p.user_id}/generate-code`, null, P);
      if (res.data.success && res.data.code) {
        setCodes(prev => ({ ...prev, [p.user_id]: { code: res.data.code, email: res.data.email } }));
        toast.success('Code generated — copy the DM below');
      } else { toast.error('Could not generate a code'); }
    } catch { toast.error('Failed to generate code'); }
  };
  const copyDM = (p) => {
    const c = codes[p.user_id]; if (!c) return;
    const first = (p.name || '').split(' ')[0] || 'there';
    const host = window.location.origin.replace(/^https?:\/\//, '');
    const previewLink = `${window.location.origin}/api/share/${p.username}`;
    const claimLink = `${window.location.origin}/claim/${p.username}`;
    const msg = `Hey ${first}, this is Offersz — we built a recruiting profile for you. Take a look first (no sign-up needed):\n\n${previewLink}\n\nIf you like it, claim it to make it live and start editing here:\n${claimLink}\nYour code: ${c.code} (expires in 24 hours)`;
    navigator.clipboard.writeText(msg);
    toast.success('DM copied — paste it into their X DMs');
  };

  // --- Roster import ---
  const scrapeRoster = async () => {
    if (!roster.url.trim()) { toast.error('Paste a HUDL roster URL'); return; }
    setRoster(r => ({ ...r, scraping: true, data: null, result: null, selected: {} }));
    try {
      const res = await axios.post(`${API}/api/admin/roster/scrape`, { roster_url: roster.url.trim() }, P);
      const players = res.data.players || [];
      const selected = {};
      players.forEach((pl, i) => { if (pl.importable && !pl.already_imported) selected[i] = true; });
      setRoster(r => ({ ...r, scraping: false, data: res.data, selected }));
      toast.success(`Found ${players.length} players (${res.data.importable_count} importable)`);
    } catch (err) {
      setRoster(r => ({ ...r, scraping: false }));
      toast.error(err.response?.data?.detail || 'Could not read that roster');
    }
  };
  const togglePlayer = (i) => setRoster(r => ({ ...r, selected: { ...r.selected, [i]: !r.selected[i] } }));
  const selectAllPlayers = (on) => setRoster(r => {
    const sel = {};
    if (on) (r.data?.players || []).forEach((pl, i) => { if (pl.importable && !pl.already_imported) sel[i] = true; });
    return { ...r, selected: sel };
  });
  const importSelected = async () => {
    const players = (roster.data?.players || []).filter((pl, i) => roster.selected[i] && pl.importable && !pl.already_imported);
    if (players.length === 0) { toast.error('Select at least one importable player'); return; }
    setRoster(r => ({ ...r, importing: true, result: null }));
    try {
      const res = await axios.post(`${API}/api/admin/roster/import`, {
        school: roster.data?.team_name || '',
        team_name: roster.data?.team_name || '',
        team_logo_url: roster.data?.team_logo_url || null,
        hudl_team_url: roster.url?.trim() || null,
        players: players.map(pl => ({
          name: pl.name, hudl_url: pl.hudl_url, positions: pl.positions || [],
          jersey_number: pl.jersey_number, height: pl.height, weight: pl.weight,
          class_year: pl.class_year, profile_image_url: pl.profile_image_url,
        })),
      }, P);
      setRoster(r => ({ ...r, importing: false, result: res.data }));
      toast.success(res.data.message);
      loadAll();
    } catch (err) {
      setRoster(r => ({ ...r, importing: false }));
      toast.error(err.response?.data?.detail || 'Import failed');
    }
  };

  // --- Single player import (by HUDL profile link) ---
  const importSinglePlayer = async () => {
    if (!addPlayer.url.trim()) { toast.error('Paste a HUDL profile URL'); return; }
    setAddPlayer(a => ({ ...a, loading: true }));
    try {
      const res = await axios.post(`${API}/api/admin/import-player`, { hudl_url: addPlayer.url.trim(), team_id: addPlayer.team_id || null }, P);
      if (res.data.success) {
        toast.success(res.data.already_imported ? 'Player already exists — opening editor' : `Added ${res.data.name} — opening editor`);
        setAddPlayer({ open: false, url: '', team_id: '', loading: false });
        editProfile({ user_id: res.data.user_id, name: res.data.name, username: res.data.username });
      }
    } catch (err) {
      setAddPlayer(a => ({ ...a, loading: false }));
      toast.error(err.response?.data?.detail || 'Could not import that player');
    }
  };

  // --- Claim codes for imported/unclaimed pages ---
  const genClaimCode = async (p) => {
    try {
      const res = await axios.post(`${API}/api/admin/profiles/${p.user_id}/claim-code`, null, P);
      if (res.data.success) {
        setClaimCodes(prev => ({ ...prev, [p.user_id]: { code: res.data.code, username: res.data.username, name: res.data.name } }));
        toast.success('Claim code generated');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate claim code'); }
  };
  const copyClaimDM = (p) => {
    const c = claimCodes[p.user_id]; if (!c) return;
    const previewLink = `${window.location.origin}/api/share/${c.username}`;
    const claimLink = `${window.location.origin}/claim/${c.username}`;
    const first = (p.name || '').split(' ')[0] || 'there';
    const msg = `Hey ${first}! Offersz built you a free recruiting page. Take a look first (no sign-up needed):\n\n${previewLink}\n\nIf you like it, claim it to make it live and start editing here:\n${claimLink}\nYour code: ${c.code}`;
    navigator.clipboard.writeText(msg);
    toast.success('Claim DM copied — paste it into their X DMs');
  };
  const deleteUser = async (uid) => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    try { await axios.delete(`${API}/api/admin/user/${uid}`, P); toast.success('User deleted'); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const setAdmin = async (uid, flag) => {
    try { await axios.post(`${API}/api/admin/users/${uid}/set-admin`, { is_admin: flag }, P); toast.success(flag ? 'Admin granted' : 'Admin revoked'); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const modAction = async (kind, uid) => {
    const reason = window.prompt(`Reason for ${kind}:`);
    if (reason === null) return;
    try { await axios.post(`${API}/api/admin/moderation/${kind}/${uid}`, { reason }, P); toast.success(`User ${kind}ned/ed`); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const approveReport = async (reportId) => {
    try { await axios.post(`${API}/api/admin/moderation/approve/${reportId}`, { reason: 'Reviewed' }, P); toast.success('Report dismissed'); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const importLegendz = async () => {
    if (!window.confirm('Import the NFL Legendz spreadsheet into THIS environment? Existing legends are updated and their offers/stats/measurables are refreshed.')) return;
    setImportingLegendz(true);
    try {
      const res = await axios.post(`${API}/api/admin/import-legendz`, null, P);
      const d = res.data;
      toast.success(`Imported: ${d.created_count} new, ${d.updated_count} updated — ${d.total_legends} legends total`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImportingLegendz(false);
    }
  };
  const deleteLegend = async (profile_id) => {
    if (!window.confirm('Remove this NFL Legend?')) return;
    try { await axios.delete(`${API}/api/admin/legends/${profile_id}`, P); toast.success('Legend removed'); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const editLegendDashboard = (l) => {
    const uid = l.user_id || l.id;
    sessionStorage.removeItem('impersonateUserId');
    sessionStorage.removeItem('impersonateName');
    sessionStorage.setItem('legendEditUserId', uid);
    sessionStorage.setItem('legendEditName', l.name || l.username || 'Legend');
    navigate('/dashboard');
  };
  // Open any player's dashboard in editable admin mode (e.g. to polish an imported page before sending)
  const editProfile = (p) => {
    const uid = p.user_id || p.id;
    sessionStorage.removeItem('impersonateUserId');
    sessionStorage.removeItem('impersonateName');
    sessionStorage.setItem('legendEditUserId', uid);
    sessionStorage.setItem('legendEditName', p.name || p.username || 'this player');
    navigate('/dashboard');
  };

  const previewDashboard = (p) => {
    const uid = p.user_id || p.id;
    const name = p.name || p.username || p.email || 'Player';
    axios.post(`${API}/api/admin/impersonation-log`, { target_user_id: uid, target_name: name, target_username: p.username || '' }, P).catch(() => {});
    sessionStorage.setItem('impersonateUserId', uid);
    sessionStorage.setItem('impersonateName', name);
    navigate('/dashboard');
  };

  if (!userId || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 text-center" data-testid="admin-portal-denied">
        <div>
          <ShieldAlert className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-2">Admin Access Only</h1>
          <p className="text-white/40 mb-6">Sign in with an admin account to access the portal.</p>
          <button onClick={() => navigate('/dashboard')} className="bg-[#16A34A] text-white font-bold px-8 py-3 rounded-full">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const Badge = ({ status }) => {
    const map = { approved: 'bg-[#16A34A]/20 text-[#4ade80]', pending: 'bg-amber-500/20 text-amber-400', rejected: 'bg-red-500/20 text-red-400' };
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[status] || 'bg-white/10 text-white/50'}`}>{(status || 'approved').toUpperCase()}</span>;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-white/40 hover:text-white" data-testid="portal-back"><ArrowLeft className="h-5 w-5" /></button>
            <div className="flex items-center gap-2"><img src="/offersz-o.png" alt="Offersz" className="w-8 h-8 rounded-full" /><span className="font-black text-lg">Offersz HQ</span></div>
          </div>
          <button onClick={() => navigate('/admin/ads')} data-testid="portal-admanager-link" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-full text-sm flex items-center gap-1.5"><DollarSign className="h-4 w-4" /> Ad Manager</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="md:w-56 flex-shrink-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible scrollbar-hide" data-testid="portal-nav">
            {NAV.map(n => (
              <button key={n.id} onClick={() => setTab(n.id)} data-testid={`portal-tab-${n.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${tab === n.id ? 'bg-[#16A34A] text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'}`}>
                {n.icon} {n.label}
                {n.id === 'profiles' && pending.length > 0 && <span className="ml-auto bg-amber-500 text-black text-[10px] font-black px-1.5 rounded-full">{pending.length}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" /></div> : (
            <>
              {/* OVERVIEW */}
              {tab === 'overview' && stats && (
                <div data-testid="portal-overview">
                  <h1 className="text-2xl font-black mb-5">Overview</h1>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      ['Total Players', stats.total_athletes, <Users className="h-5 w-5" />],
                      ['Total Users', stats.total_users, <UserCog className="h-5 w-5" />],
                      ['Highlight Videos', stats.total_videos, <Film className="h-5 w-5" />],
                      ['Recent Signups', stats.recent_signups, <Clock className="h-5 w-5" />],
                      ['Verified Players', stats.verified_athletes, <BadgeCheck className="h-5 w-5" />],
                      ['Pending Approval', pending.length, <Flag className="h-5 w-5" />],
                    ].map(([label, val, icon]) => (
                      <div key={label} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5">
                        <div className="text-[#16A34A] mb-2">{icon}</div>
                        <p className="text-3xl font-black">{val ?? 0}</p>
                        <p className="text-white/40 text-xs mt-1">{label}</p>
                      </div>
                    ))}
                  </div>
                  {pending.length > 0 && <button onClick={() => setTab('profiles')} className="mt-5 bg-amber-500/20 text-amber-300 font-semibold text-sm px-5 py-3 rounded-xl w-full text-left">{pending.length} profile(s) waiting for approval →</button>}
                </div>
              )}

              {/* PROFILES */}
              {tab === 'profiles' && (
                <div data-testid="portal-profiles">
                  <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                    <h1 className="text-2xl font-black">Player Profiles</h1>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setAddPlayer(a => ({ ...a, open: true }))} data-testid="open-add-player" className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><UserPlus className="h-4 w-4" /> Add Player by HUDL Link</button>
                      <button onClick={() => setRoster(r => ({ ...r, open: true }))} data-testid="open-roster-import" className="bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Import from HUDL Roster</button>
                    </div>
                  </div>
                  {claimRequests.length > 0 && (
                    <div className="mb-6" data-testid="claim-requests-group">
                      <p className="text-amber-400 font-bold text-sm mb-2 flex items-center gap-1.5"><Megaphone className="h-4 w-4" /> Claim Requests ({claimRequests.length})</p>
                      <p className="text-white/30 text-xs mb-2">Players who asked for a claim code (from a team page or their own claim page). Verify it's really them, then generate a code and DM/email it.</p>
                      <div className="space-y-2">
                        {claimRequests.map(req => {
                          const athlete = athletes.find(a => a.user_id === req.player_user_id);
                          return (
                          <div key={req.id} data-testid={`claim-request-${req.id}`} className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="min-w-0">
                                <p className="font-bold text-white">{req.player_name} <span className="text-white/30 font-normal text-xs">@{req.player_username}</span>
                                  <span data-testid={`req-source-${req.id}`} className={`ml-2 align-middle text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${req.source === 'claim_page' ? 'bg-[#16A34A]/20 text-[#4ade80]' : 'bg-sky-500/20 text-sky-300'}`}>{req.source === 'claim_page' ? 'Claim Page' : 'Team Page'}</span>
                                </p>
                                <p className="text-white/50 text-xs">{req.team_name ? `${req.team_name} · ` : ''}{req.contact_email || ''}{req.contact_x ? ` · X @${req.contact_x}` : ''}</p>
                                {req.message && <p className="text-white/40 text-xs italic mt-0.5">"{req.message}"</p>}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <a href={`/preview/${req.player_username}`} target="_blank" rel="noreferrer" title="Preview their page" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-sky-300"><Eye className="h-4 w-4" /></a>
                                {athlete && !claimCodes[req.player_user_id] && (
                                  <button onClick={() => genClaimCode(athlete)} data-testid={`req-gen-code-${req.id}`} className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Generate code</button>
                                )}
                                {athlete && claimCodes[req.player_user_id] && (
                                  <button onClick={() => copyClaimDM(athlete)} data-testid={`req-copy-dm-${req.id}`} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Copy DM</button>
                                )}
                                <button onClick={() => resolveClaimRequest(req.id)} data-testid={`req-resolve-${req.id}`} className="bg-white/5 hover:bg-white/15 text-white/70 text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Done</button>
                              </div>
                            </div>
                            {claimCodes[req.player_user_id] && (
                              <div className="mt-2 bg-black/30 rounded-lg px-3 py-2 text-xs"><span className="text-white/40">Code: </span><span className="font-mono font-bold text-[#4ade80] tracking-widest">{claimCodes[req.player_user_id].code}</span></div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {athletes.filter(a => a.is_claimed === false && !a.is_showcase).length > 0 && (
                    <div className="mb-6" data-testid="unclaimed-group">
                      <p className="text-sky-400 font-bold text-sm mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Unclaimed — Imported Pages ({athletes.filter(a => a.is_claimed === false && !a.is_showcase).length})</p>
                      <p className="text-white/30 text-xs mb-2">These pages were imported from a roster and are hidden until the real player claims them. Generate a code and DM it to the player on X.</p>
                      <div className="space-y-2">
                        {athletes.filter(a => a.is_claimed === false && !a.is_showcase).map(p => (
                          <div key={p.id} data-testid={`unclaimed-${p.id}`} className="bg-sky-500/5 border border-sky-500/20 rounded-2xl p-3">
                            <div className="flex items-center gap-3">
                              <Avatar p={p} />
                              <div className="flex-1 min-w-0"><p className="font-bold truncate">{p.name}</p><p className="text-white/40 text-xs truncate">offersz.co/{p.username} · {p.school || '—'}</p></div>
                              {p.hudl_url && <a href={p.hudl_url} target="_blank" rel="noreferrer" title="Open HUDL profile" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-orange-400"><ExternalLink className="h-4 w-4" /></a>}
                              <a href={`/preview/${p.username}`} target="_blank" rel="noreferrer" data-testid={`preview-unclaimed-${p.id}`} title="Preview this page" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-sky-300"><Eye className="h-4 w-4" /></a>
                              <button onClick={() => editProfile(p)} data-testid={`edit-unclaimed-${p.id}`} title="Edit & polish this page" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/80"><Pencil className="h-4 w-4" /></button>
                              <button onClick={() => genClaimCode(p)} data-testid={`gen-claim-${p.id}`} className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Generate claim code</button>
                              <span className="bg-sky-500/20 text-sky-300 text-[10px] font-black px-2 py-1 rounded-full">UNCLAIMED</span>
                            </div>
                            {claimCodes[p.user_id] && (
                              <div data-testid={`claim-box-${p.id}`} className="mt-3 bg-black/40 border border-sky-500/30 rounded-xl p-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-white/40 text-[10px] uppercase tracking-wider">Claim code (DM with the claim link)</p>
                                  <p className="text-2xl font-black tracking-[0.3em] text-sky-300">{claimCodes[p.user_id].code}</p>
                                </div>
                                <button onClick={() => copyClaimDM(p)} data-testid={`copy-claim-dm-${p.id}`} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1 flex-shrink-0"><ExternalLink className="h-3.5 w-3.5" /> Copy X DM</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {pending.length > 0 && (
                    <div className="mb-6">
                      <p className="text-amber-400 font-bold text-sm mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Pending Approval ({pending.length})</p>
                      <div className="space-y-2">
                        {pending.map(p => (
                          <div key={p.id} data-testid={`pending-${p.id}`} className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-3 flex items-center gap-3">
                            <input type="checkbox" checked={!!sel[p.id]} onChange={() => toggleSel(p)} data-testid={`select-${p.id}`} className="w-4 h-4 accent-[#16A34A] flex-shrink-0" />
                            <Avatar p={p} />
                            <div className="flex-1 min-w-0"><p className="font-bold truncate">{p.name}</p><p className="text-white/40 text-xs truncate">offersz.co/{p.username} · {p.school}</p></div>
                            <button onClick={() => navigate(`/profile/${p.username}`)} className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><Eye className="h-4 w-4" /></button>
                            <button onClick={() => setApproval(p.id, 'approved')} data-testid={`approve-${p.id}`} className="bg-[#16A34A] hover:bg-[#0F5132] text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Approve</button>
                            <button onClick={() => setApproval(p.id, 'rejected')} data-testid={`reject-${p.id}`} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><X className="h-3.5 w-3.5" /> Reject</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {athletes.filter(a => a.is_verified === false && a.is_claimed !== false && !a.is_showcase).length > 0 && (
                    <div className="mb-6" data-testid="needs-verification-group">
                      <p className="text-orange-400 font-bold text-sm mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Needs Account Verification ({athletes.filter(a => a.is_verified === false && a.is_claimed !== false && !a.is_showcase).length})</p>
                      <p className="text-white/30 text-xs mb-2">These athletes signed up but haven't verified by email or X DM yet — their profiles stay private until they do.</p>
                      <div className="space-y-2">
                        {athletes.filter(a => a.is_verified === false && a.is_claimed !== false && !a.is_showcase).map(p => (
                          <div key={p.id} data-testid={`unverified-${p.id}`} className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-3">
                            <div className="flex items-center gap-3">
                              <Avatar p={p} />
                              <div className="flex-1 min-w-0"><p className="font-bold truncate">{p.name}</p><p className="text-white/40 text-xs truncate">offersz.co/{p.username} · {p.email}</p></div>
                              {p.x_url && <a href={p.x_url} target="_blank" rel="noreferrer" title="Open X profile to DM" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-sky-400"><Twitter className="h-4 w-4" /></a>}
                              <button onClick={() => genCode(p)} data-testid={`gen-code-${p.id}`} className="bg-[#16A34A] hover:bg-[#0F5132] text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Generate code</button>
                              <span className="bg-orange-500/20 text-orange-300 text-[10px] font-black px-2 py-1 rounded-full">UNVERIFIED</span>
                            </div>
                            {codes[p.user_id] && (
                              <div data-testid={`code-box-${p.id}`} className="mt-3 bg-black/40 border border-[#16A34A]/30 rounded-xl p-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-white/40 text-[10px] uppercase tracking-wider">6-digit code (DM this on X)</p>
                                  <p className="text-2xl font-black tracking-[0.3em] text-[#4ade80]">{codes[p.user_id].code}</p>
                                </div>
                                <button onClick={() => copyDM(p)} data-testid={`copy-dm-${p.id}`} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1 flex-shrink-0"><ExternalLink className="h-3.5 w-3.5" /> Copy X DM</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white/40 text-sm">All Profiles ({athletes.length})</p>
                    <div className="flex items-center gap-3 text-xs">
                      <button onClick={() => setSel(() => { const n = {}; athletes.forEach(p => { n[p.id] = { id: p.id, user_id: p.user_id }; }); return n; })} data-testid="select-all-profiles" className="text-[#4ade80] font-semibold hover:underline">Select all</button>
                      <button onClick={clearSel} className="text-white/40 font-semibold hover:underline">Clear</button>
                    </div>
                  </div>
                  <div className="space-y-2" data-testid="all-profiles">
                    {athletes.map(p => (
                      <div key={p.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-3">
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={!!sel[p.id]} onChange={() => toggleSel(p)} data-testid={`select-all-${p.id}`} className="w-4 h-4 accent-[#16A34A] flex-shrink-0" />
                          <Avatar p={p} />
                          <div className="flex-1 min-w-0"><p className="font-bold truncate flex items-center gap-1.5">{p.name} {p.verified && <BadgeCheck className="h-4 w-4 text-[#4ade80]" />}</p><p className="text-white/40 text-xs truncate">offersz.co/{p.username} · {p.school}</p></div>
                          {p.is_verified === false && !p.is_showcase && <span className="bg-orange-500/20 text-orange-300 text-[10px] font-black px-2 py-1 rounded-full">UNVERIFIED</span>}
                          {p.is_showcase && <span className="bg-purple-500/20 text-purple-300 text-[10px] font-black px-2 py-1 rounded-full">SHOWCASE</span>}
                          <Badge status={p.approval_status} />
                          {!p.is_showcase && <button onClick={() => genCode(p)} data-testid={`gen-code-all-${p.id}`} title="Generate a 6-digit identity code to DM them" className="p-2 rounded-full bg-[#16A34A]/15 hover:bg-[#16A34A]/25 text-[#4ade80]"><KeyRound className="h-4 w-4" /></button>}
                          <button onClick={() => editProfile(p)} data-testid={`edit-profile-${p.id}`} title="Edit & polish this profile" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/80"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => previewDashboard(p)} data-testid={`preview-dashboard-${p.id}`} title="Preview their dashboard (read-only)" className="p-2 rounded-full bg-[#16A34A]/15 hover:bg-[#16A34A]/25 text-[#4ade80]"><LayoutDashboard className="h-4 w-4" /></button>
                          <button onClick={() => navigate(`/profile/${p.username}`)} title="View public profile" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><Eye className="h-4 w-4" /></button>
                          <button onClick={() => toggleVerify(p.id, !p.verified)} title="Toggle verified badge" className={`p-2 rounded-full ${p.verified ? 'bg-[#16A34A]/20 text-[#4ade80]' : 'bg-white/5 text-white/70 hover:bg-white/15'}`}><BadgeCheck className="h-4 w-4" /></button>
                          <button onClick={() => deleteUser(p.user_id)} title="Delete" className="p-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        {codes[p.user_id] && (
                          <div data-testid={`code-box-all-${p.id}`} className="mt-3 bg-black/40 border border-[#16A34A]/30 rounded-xl p-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-white/40 text-[10px] uppercase tracking-wider">6-digit identity code (DM this on X)</p>
                              <p className="text-2xl font-black tracking-[0.3em] text-[#4ade80]">{codes[p.user_id].code}</p>
                            </div>
                            <button onClick={() => copyDM(p)} data-testid={`copy-dm-all-${p.id}`} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1 flex-shrink-0"><ExternalLink className="h-3.5 w-3.5" /> Copy X DM</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TEAMS */}
              {tab === 'teams' && (
                <div data-testid="portal-teams">
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-black mb-1">Teams & Rosters</h2>
                      <p className="text-white/40 text-sm">Manage team pages, logos, and rosters. A player can belong to more than one roster (e.g. their high school + a 7-on-7 team).</p>
                    </div>
                    <button onClick={createRoster} data-testid="new-roster-btn" className="bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5 whitespace-nowrap"><Plus className="h-4 w-4" /> New Roster</button>
                  </div>

                  {teams.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center text-white/40" data-testid="teams-empty">
                      No teams yet. Import a HUDL roster or tap "New Roster" to create one.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {teams.map(t => {
                        const members = teamMembers[t.id] || [];
                        const memberIds = new Set(members.map(m => m.user_id));
                        const addable = athletes.filter(a => !memberIds.has(a.user_id) && !a.is_showcase && !a.is_admin);
                        const open = expandedTeam === t.id;
                        return (
                        <div key={t.id} data-testid={`team-card-${t.id}`} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                          <div className="flex items-center gap-4 flex-wrap">
                            {t.logo_url
                              ? <img src={t.logo_url} alt="" className="w-14 h-14 rounded-xl object-contain bg-white/5 p-1 border border-white/10" />
                              : <div className="w-14 h-14 rounded-xl bg-[#16A34A] flex items-center justify-center font-black">{(t.school_name || 'T').slice(0,2).toUpperCase()}</div>}
                            <div className="flex-1 min-w-0">
                              <p className="font-black text-white truncate flex items-center gap-2">{t.school_name}
                                {t.type === 'seven_on_seven' && <span className="text-[10px] font-black uppercase tracking-wider bg-[#16A34A]/20 text-[#4ade80] px-1.5 py-0.5 rounded">7v7</span>}
                                {t.type === 'club' && <span className="text-[10px] font-black uppercase tracking-wider bg-sky-500/20 text-sky-300 px-1.5 py-0.5 rounded">Club</span>}
                              </p>
                              <p className="text-white/40 text-xs">{t.claimed_count}/{t.player_count} claimed · {t.completion_percentage}%</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <a href={`/team/${t.slug}`} target="_blank" rel="noreferrer" data-testid={`team-view-${t.id}`} title="View public team page" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-sky-300"><ExternalLink className="h-4 w-4" /></a>
                              <button onClick={() => renameTeam(t)} data-testid={`team-rename-${t.id}`} title="Rename team" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><Pencil className="h-4 w-4" /></button>
                              <label title="Upload team logo" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70 cursor-pointer" data-testid={`team-logo-upload-${t.id}`}>
                                <ImageIcon className="h-4 w-4" />
                                <input type="file" accept="image/*" className="hidden" onChange={e => uploadTeamLogo(t.slug, e.target.files?.[0])} />
                              </label>
                              <button onClick={() => toggleTeam(t.id)} data-testid={`team-manage-${t.id}`} className="text-xs font-bold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full">{open ? 'Close' : 'Manage roster'}</button>
                            </div>
                          </div>
                          {open && (
                            <div className="mt-4 border-t border-white/10 pt-4" data-testid={`team-roster-${t.id}`}>
                              <p className="text-xs font-bold text-white/60 uppercase tracking-wider mb-2">Roster ({members.length})</p>
                              <div className="space-y-1.5 mb-4 max-h-60 overflow-y-auto">
                                {members.length === 0 && <p className="text-white/30 text-sm">No players on this roster yet.</p>}
                                {members.map(pl => (
                                  <div key={pl.user_id} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2">
                                    <span className="text-sm text-white/80 truncate">{pl.name} <span className="text-white/30">@{pl.username}</span>
                                      {pl.is_primary && <span className="ml-2 text-[10px] font-black uppercase tracking-wider bg-white/10 text-white/50 px-1.5 py-0.5 rounded">Primary</span>}
                                    </span>
                                    <button onClick={() => removeFromRoster(t.id, pl.user_id)} data-testid={`team-remove-${pl.user_id}`} className="text-xs font-bold text-red-300 hover:text-red-200 whitespace-nowrap">Remove</button>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs font-bold text-white/60 uppercase tracking-wider mb-2">Add a player (stays on their other rosters too)</p>
                              <select onChange={e => { if (e.target.value) { addToRoster(t.id, e.target.value); e.target.value = ''; } }} data-testid={`team-add-player-${t.id}`}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                                <option value="">Select a player to add…</option>
                                {addable.map(pl => <option key={pl.user_id} value={pl.user_id} className="bg-[#0d0f0e]">{pl.name} (@{pl.username})</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Duplicate finder */}
                  <div className="mt-8" data-testid="duplicates-panel">
                    <h3 className="text-lg font-black mb-1 flex items-center gap-2"><Flag className="h-5 w-5 text-amber-400" /> Duplicate Finder</h3>
                    <p className="text-white/40 text-sm mb-4">Players sharing the same HUDL link or name. Keep one, remove the rest.</p>
                    {duplicates.length === 0 ? (
                      <div className="bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-2xl p-4 text-[#4ade80] text-sm font-bold" data-testid="duplicates-none">✓ No duplicate profiles found.</div>
                    ) : (
                      <div className="space-y-3">
                        {duplicates.map((g, gi) => (
                          <div key={gi} data-testid={`dup-group-${gi}`} className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
                            <p className="text-amber-300 text-xs font-bold mb-2">{g.reason}</p>
                            <div className="space-y-1.5">
                              {g.players.map(pl => (
                                <div key={pl.user_id} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2">
                                  <div className="min-w-0">
                                    <span className="text-sm text-white/80">{pl.name} <span className="text-white/30">@{pl.username}</span></span>
                                    <span className="text-xs text-white/30 ml-2">{pl.is_verified ? 'claimed/live' : 'unclaimed'}</span>
                                  </div>
                                  <button onClick={() => resolveDuplicates(pl.user_id, g.players.filter(x => x.user_id !== pl.user_id))}
                                    data-testid={`dup-keep-${pl.user_id}`} className="text-xs font-bold bg-[#0F5132] hover:bg-[#0B3B24] text-white px-3 py-1.5 rounded-full whitespace-nowrap">Keep this, remove others</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}


              {/* USERS */}
              {tab === 'users' && (
                <div data-testid="portal-users">
                  <h1 className="text-2xl font-black mb-5">Users & Admins ({users.length})</h1>
                  <div className="space-y-2">
                    {users.map(u => (
                      <div key={u.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#16A34A]/20 flex items-center justify-center text-[#4ade80] font-black">{(u.name || u.email || '?').charAt(0).toUpperCase()}</div>
                        <div className="flex-1 min-w-0"><p className="font-bold truncate">{u.name || u.username || '—'}</p><p className="text-white/40 text-xs truncate">{u.email}</p></div>
                        {u.is_admin && <span className="bg-[#16A34A]/20 text-[#4ade80] text-[10px] font-bold px-2 py-0.5 rounded-full">ADMIN</span>}
                        <button onClick={() => previewDashboard(u)} data-testid={`user-preview-${u.id}`} title="Preview their dashboard (read-only)" className="p-2 rounded-full bg-[#16A34A]/15 hover:bg-[#16A34A]/25 text-[#4ade80]"><LayoutDashboard className="h-4 w-4" /></button>
                        <button onClick={() => setAdmin(u.id, !u.is_admin)} data-testid={`setadmin-${u.id}`} className="bg-white/5 hover:bg-white/15 text-white/80 text-xs font-bold px-3 py-2 rounded-full">{u.is_admin ? 'Revoke' : 'Make Admin'}</button>
                        {u.id !== userId && <button onClick={() => deleteUser(u.id)} className="p-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MODERATION */}
              {tab === 'moderation' && (
                <div data-testid="portal-moderation">
                  <h1 className="text-2xl font-black mb-5">Moderation</h1>
                  {reports.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-2xl py-16 text-center text-white/40">No reports to review. All clear.</div>
                  ) : (
                    <div className="space-y-2">
                      {reports.map(r => (
                        <div key={r.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-4">
                          <div className="flex items-center gap-2 mb-2"><Flag className="h-4 w-4 text-red-400" /><p className="font-bold text-sm">{r.reason || 'Reported'}</p><span className="text-white/30 text-xs ml-auto">user: {r.reported_user_id?.slice(0, 8)}</span></div>
                          {r.details && <p className="text-white/50 text-sm mb-3">{r.details}</p>}
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => approveReport(r.id)} className="bg-white/5 hover:bg-white/15 text-xs font-bold px-3 py-1.5 rounded-full">Dismiss</button>
                            <button onClick={() => modAction('warn', r.reported_user_id)} className="bg-amber-500/20 text-amber-300 text-xs font-bold px-3 py-1.5 rounded-full">Warn</button>
                            <button onClick={() => modAction('suspend', r.reported_user_id)} className="bg-orange-500/20 text-orange-300 text-xs font-bold px-3 py-1.5 rounded-full">Suspend</button>
                            <button onClick={() => modAction('ban', r.reported_user_id)} className="bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1.5 rounded-full">Ban</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* LEGENDS */}
              {tab === 'legends' && (
                <div data-testid="portal-legends">
                  <div className="flex items-center justify-between mb-5">
                    <h1 className="text-2xl font-black">NFL Legendz ({legends.length})</h1>
                    <div className="flex items-center gap-2">
                      <button onClick={importLegendz} disabled={importingLegendz} data-testid="import-legendz-btn" className="bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5">{importingLegendz ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import from Spreadsheet</button>
                      <button onClick={() => setLegendModal({})} data-testid="new-legend-btn" className="bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add Legend</button>
                    </div>
                  </div>
                  <p className="text-white/40 text-sm mb-4">These NFL showcase profiles appear in the Wall of Fame on the landing page.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {legends.map(l => (
                      <div key={l.id} data-testid={`legend-${l.id}`} className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 flex items-center gap-3">
                        <Avatar p={l} />
                        <div className="flex-1 min-w-0"><p className="font-bold truncate">{l.name}</p><p className="text-white/40 text-xs truncate">{(l.positions || [])[0]} · {l.school}</p>{l.nfl_team && <p className="text-[#4ade80] text-xs font-bold truncate">{l.nfl_team}</p>}</div>
                        <button onClick={() => navigate(`/profile/${l.username}`)} className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><ExternalLink className="h-4 w-4" /></button>
                        <button onClick={() => editLegendDashboard(l)} data-testid={`edit-legend-dashboard-${l.id}`} title="Edit full profile (all fields)" className="p-2 rounded-full bg-[#16A34A]/15 hover:bg-[#16A34A]/25 text-[#4ade80]"><LayoutDashboard className="h-4 w-4" /></button>
                        <button onClick={() => setLegendModal(l)} data-testid={`edit-legend-${l.id}`} title="Quick edit basics" className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => deleteLegend(l.id)} className="p-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AUDIT LOG */}
              {tab === 'audit' && (
                <div data-testid="portal-audit">
                  <h1 className="text-2xl font-black mb-2">Impersonation Audit Log</h1>
                  <p className="text-white/40 text-sm mb-5">Every time an admin previews a player's dashboard, it's recorded here.</p>
                  {logs.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-2xl py-16 text-center text-white/40">No preview activity recorded yet.</div>
                  ) : (
                    <div className="space-y-2" data-testid="audit-list">
                      {logs.map(log => (
                        <div key={log.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-3 flex items-center gap-3" data-testid={`audit-${log.id}`}>
                          <div className="w-9 h-9 rounded-full bg-[#16A34A]/20 flex items-center justify-center text-[#4ade80] flex-shrink-0"><Clock className="h-4 w-4" /></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm"><span className="font-bold">{log.admin_username || log.admin_email || 'Admin'}</span> <span className="text-white/40">previewed</span> <span className="font-bold">{log.target_name || log.target_username}</span></p>
                            <p className="text-white/30 text-xs">{log.target_username ? `offersz.co/${log.target_username}` : log.target_user_id}</p>
                          </div>
                          <span className="text-white/30 text-xs flex-shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {legendModal !== null && <LegendModal userId={userId} legend={legendModal} onClose={() => setLegendModal(null)} onSaved={() => { setLegendModal(null); loadAll(); }} />}

      {/* Bulk action bar */}
      {tab === 'profiles' && selList.length > 0 && (
        <div data-testid="bulk-action-bar" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#111] border border-white/15 rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-2">
          <span className="text-white text-sm font-bold px-2">{selList.length} selected</span>
          <button onClick={() => bulkSetApproval('approved')} data-testid="bulk-approve" className="bg-[#16A34A] hover:bg-[#0F5132] text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Approve</button>
          <button onClick={() => bulkSetApproval('rejected')} data-testid="bulk-reject" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><X className="h-3.5 w-3.5" /> Reject</button>
          <button onClick={bulkDelete} data-testid="bulk-delete" className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          <button onClick={clearSel} data-testid="bulk-clear" className="text-white/40 hover:text-white text-xs font-semibold px-2">Clear</button>
        </div>
      )}

      {/* Add Single Player Modal */}
      {addPlayer.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4" data-testid="add-player-modal">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-3xl w-full max-w-lg my-16">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div><h2 className="text-xl font-black">Add Player by HUDL Link</h2><p className="text-white/40 text-xs">Paste a single HUDL profile URL to create a page and start editing.</p></div>
              <button onClick={() => setAddPlayer({ open: false, url: '', loading: false })} data-testid="add-player-close" className="text-white/40 hover:text-white p-2"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <input value={addPlayer.url} onChange={e => setAddPlayer(a => ({ ...a, url: e.target.value }))} placeholder="https://www.hudl.com/profile/..."
                data-testid="add-player-url-input" onKeyPress={e => e.key === 'Enter' && !addPlayer.loading && importSinglePlayer()}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 h-12 text-white text-sm focus:border-[#16A34A] outline-none" />
              <p className="text-white/30 text-xs">We'll pull their name, photo, position, class year, size and highlights, then open the editor so you can polish before sending the claim link.</p>
              {teams.length > 0 && (
                <select value={addPlayer.team_id || ''} onChange={e => setAddPlayer(a => ({ ...a, team_id: e.target.value }))} data-testid="add-player-team-select"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 h-12 text-white text-sm focus:border-[#16A34A] outline-none">
                  <option value="">No team (assign later)</option>
                  {teams.map(t => <option key={t.id} value={t.id} className="bg-[#0d0f0e]">{t.school_name}</option>)}
                </select>
              )}
              <button onClick={importSinglePlayer} disabled={addPlayer.loading || !addPlayer.url.trim()} data-testid="add-player-submit"
                className="w-full bg-[#16A34A] hover:bg-[#0F5132] disabled:opacity-50 text-white font-bold h-12 rounded-xl flex items-center justify-center gap-2">
                {addPlayer.loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Importing…</> : <><UserPlus className="h-5 w-5" /> Add & edit</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roster Import Modal */}
      {roster.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="roster-modal">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-3xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0f0f0f] rounded-t-3xl">
              <div><h2 className="text-xl font-black">Import from HUDL Roster</h2><p className="text-white/40 text-xs">Paste a team roster URL and onboard the whole team at once.</p></div>
              <button onClick={() => setRoster(r => ({ ...r, open: false }))} data-testid="roster-close" className="text-white/40 hover:text-white p-2"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex gap-2">
                <input value={roster.url} onChange={e => setRoster(r => ({ ...r, url: e.target.value }))} placeholder="https://www.hudl.com/team/.../roster"
                  data-testid="roster-url-input" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 h-11 text-white text-sm focus:border-[#16A34A] outline-none" />
                <button onClick={scrapeRoster} disabled={roster.scraping} data-testid="roster-scrape-btn" className="bg-[#16A34A] hover:bg-[#0F5132] disabled:opacity-50 text-white font-bold px-5 rounded-xl text-sm flex items-center gap-2">
                  {roster.scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Scrape
                </button>
              </div>

              {roster.result && (
                <div data-testid="roster-result" className="bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-xl p-3 text-sm">
                  <p className="font-bold text-[#4ade80]">{roster.result.message}</p>
                  {roster.result.team_url && (
                    <a href={roster.result.team_url} target="_blank" rel="noreferrer" data-testid="roster-result-team-link"
                      className="inline-flex items-center gap-1.5 mt-1.5 text-xs font-bold text-sky-300 hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" /> View team page: offersz.co{roster.result.team_url}
                    </a>
                  )}
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {roster.result.results.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-white/70">{r.name}</span>
                        <span className={r.status === 'imported' ? 'text-[#4ade80]' : r.status === 'skipped' ? 'text-amber-400' : 'text-red-400'}>{r.status}{r.reason ? ` — ${r.reason}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {roster.data && !roster.result && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      {roster.data.team_name && <p className="font-bold text-sm">{roster.data.team_name}</p>}
                      <p className="text-white/40 text-xs" data-testid="roster-counts">
                        {Object.values(roster.selected).filter(Boolean).length} selected · {roster.data.importable_count} importable · {roster.data.total} on roster
                      </p>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <button onClick={() => selectAllPlayers(true)} className="text-[#4ade80] font-semibold hover:underline">Select all</button>
                      <button onClick={() => selectAllPlayers(false)} className="text-white/40 font-semibold hover:underline">None</button>
                    </div>
                  </div>

                  <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="roster-player-list">
                    {roster.data.players.map((pl, i) => {
                      const disabled = !pl.importable || pl.already_imported;
                      return (
                        <label key={i} data-testid={`roster-player-${i}`} className={`flex items-center gap-3 p-2.5 rounded-xl border ${disabled ? 'border-white/5 opacity-50' : roster.selected[i] ? 'border-[#16A34A]/40 bg-[#16A34A]/5' : 'border-white/10 bg-white/[0.02]'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input type="checkbox" disabled={disabled} checked={!!roster.selected[i]} onChange={() => togglePlayer(i)} data-testid={`roster-check-${i}`} className="w-4 h-4 accent-[#16A34A]" />
                          {pl.profile_image_url ? <img src={pl.profile_image_url} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/40">{(pl.name || '?').charAt(0)}</div>}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{pl.name}{pl.jersey_number ? ` · #${pl.jersey_number}` : ''}</p>
                            <p className="text-white/40 text-xs truncate">{[pl.positions?.join('/'), pl.class_year, pl.height, pl.weight ? `${pl.weight} lbs` : null].filter(Boolean).join(' · ') || '—'}</p>
                          </div>
                          {pl.already_imported && <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">IMPORTED</span>}
                          {!pl.importable && !pl.already_imported && <span className="text-[10px] font-bold text-white/40 bg-white/10 px-2 py-0.5 rounded-full">NO HUDL</span>}
                        </label>
                      );
                    })}
                  </div>

                  <button onClick={importSelected} disabled={roster.importing || Object.values(roster.selected).filter(Boolean).length === 0} data-testid="roster-import-btn"
                    className="w-full bg-[#16A34A] hover:bg-[#0F5132] disabled:opacity-50 text-white font-bold h-12 rounded-xl flex items-center justify-center gap-2">
                    {roster.importing ? <><Loader2 className="h-5 w-5 animate-spin" /> Importing…</> : <>Import {Object.values(roster.selected).filter(Boolean).length} selected</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ p }) => (
  <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
    {p.profile_picture_url ? <img src={img(p.profile_picture_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#16A34A] flex items-center justify-center text-white font-black">{(p.name || '?').charAt(0)}</div>}
  </div>
);

const LegendModal = ({ userId, legend, onClose, onSaved }) => {
  const editing = !!legend.id;
  const [f, setF] = useState({
    name: legend.name || '', username: legend.username || '', school: legend.school || '',
    positions: (legend.positions || []).join(', '), nfl_team: legend.nfl_team || '',
    class_year: legend.class_year || '', height: legend.height || '', weight: legend.weight || '',
    profile_picture_url: legend.profile_picture_url || '', hudl_url: legend.hudl_url || '',
    x_url: legend.x_url || '', about_me: legend.about_me || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/api/admin/ads/upload-creative`, fd, { params: { admin_user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } });
      setF(p => ({ ...p, profile_picture_url: res.data.image_url })); toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.name.trim() || !f.username.trim()) { toast.error('Name and username required'); return; }
    setSaving(true);
    const payload = {
      name: f.name, username: f.username, school: f.school,
      positions: f.positions.split(',').map(s => s.trim()).filter(Boolean),
      nfl_team: f.nfl_team, class_year: f.class_year ? parseInt(f.class_year) : null,
      height: f.height || null, weight: f.weight || null, profile_picture_url: f.profile_picture_url,
      hudl_url: f.hudl_url, x_url: f.x_url, about_me: f.about_me,
    };
    try {
      if (editing) await axios.put(`${API}/api/admin/legends/${legend.id}`, payload, { params: { admin_user_id: userId } });
      else await axios.post(`${API}/api/admin/legends`, payload, { params: { admin_user_id: userId } });
      toast.success(editing ? 'Legend updated' : 'Legend added'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  const F = ({ label, k, ph }) => (
    <div>
      <label className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">{label}</label>
      <input value={f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.value }))} placeholder={ph} data-testid={`legend-${k}`}
        className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg mx-4 mb-4 sm:mb-0 bg-zinc-900 border border-white/10 rounded-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="legend-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/30"><X className="h-5 w-5" /></button>
        <h3 className="font-black text-lg mb-5">{editing ? 'Edit NFL Legend' : 'Add NFL Legend'}</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center flex-shrink-0">
              {f.profile_picture_url ? <img src={img(f.profile_picture_url)} alt="" className="w-full h-full object-cover" /> : <Upload className="h-5 w-5 text-white/20" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="legend-photo-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-4 py-2 rounded-xl flex items-center gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Photo
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3"><F label="Name *" k="name" ph="Josh Allen" /><F label="Username *" k="username" ph="JoshAllen" /></div>
          <div className="grid grid-cols-2 gap-3"><F label="High School" k="school" ph="Firebaugh HS" /><F label="NFL Team" k="nfl_team" ph="Buffalo Bills" /></div>
          <div className="grid grid-cols-3 gap-3"><F label="Positions" k="positions" ph="QB" /><F label="Class Year" k="class_year" ph="2014" /><F label="Height" k="height" ph="6'5" /></div>
          <div className="grid grid-cols-2 gap-3"><F label="Weight" k="weight" ph="237" /><F label="HUDL URL" k="hudl_url" ph="https://hudl.com/..." /></div>
          <F label="X / Twitter URL" k="x_url" ph="https://x.com/..." />
          <div>
            <label className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">Bio</label>
            <textarea value={f.about_me} onChange={e => setF(p => ({ ...p, about_me: e.target.value }))} rows={2} data-testid="legend-about_me"
              className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none resize-none" />
          </div>
          <button onClick={save} disabled={saving} data-testid="legend-save" className="w-full bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold py-3 rounded-xl text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? 'Save Changes' : 'Add Legend'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
